import { t } from 'i18next';
import { toast } from 'react-toastify';
import { apiCall } from '~/providers';
import { ExceptionDut4, ScheduleDut4 } from '~/providers/types/api-private';

interface DayExcept {
  BEGIN_TIME: string;
  END_TIME: string;
}

const compareTimes = (a: DayExcept, b: DayExcept) => (
  (a.BEGIN_TIME <= b.BEGIN_TIME && b.BEGIN_TIME <= a.END_TIME)
  || (b.BEGIN_TIME <= a.BEGIN_TIME && a.BEGIN_TIME <= b.END_TIME)
);

const hasHoursConflicts = (excepts: DayExcept[]) => {
  for (let i = 0; i < excepts.length; i++) {
    for (let j = i + 1; j < excepts.length; j++) {
      if (compareTimes(excepts[i], excepts[j])) return true;
    }
  }
  return false;
};

type Command = 'FAN' | 'COOL' | 'OFF';
type SpAction = string;

interface Mode {
  opmode: string;
  default_cmd?: Command;
  setpoint?: number;
  temp_trigger?: number;
  sp_action?: SpAction;
  sp_intv?: number;
  hist_sup?: number;
  hist_inf?: number;
  ltc_cmd?: Command;
  ltc_degree?: number;
  lti_cmd?: Command;
  lti_degree?: number;
}

interface ScheduleItem {
  start: string; // formato "HH:mm"
  end: string; // formato "HH:mm"
  mode: Mode[];
}

export interface ScheduleDut4Command {
  days: string[];
  default_mode?: string; // É utilizado apenas para o envio da programação, para o DUT entender qual modo deve ser aplicado caso a programação esteja desativada ou para os horários que não possuem uma programação específica. Não é utilizado para exibir a programação na interface
  schedule: ScheduleItem[];
}

function scheduleDut4ToMode(schedule: ScheduleDut4): Mode {
  return {
    opmode: schedule.CTRLOPER ?? '0_DESLIGAR',
    ...(schedule.DEFAULT_COMMAND != null && { default_cmd: schedule.DEFAULT_COMMAND as Command }),
    ...(schedule.SETPOINT != null && { setpoint: schedule.SETPOINT }),
    ...(schedule.TEMP_TRIGGER != null && { temp_trigger: schedule.TEMP_TRIGGER }),
    ...(schedule.SP_ACTION != null && { sp_action: schedule.SP_ACTION }),
    ...(schedule.SP_INTV != null && { sp_intv: schedule.SP_INTV }),
    ...(schedule.UPPER_HYSTERESIS != null && { hist_sup: schedule.UPPER_HYSTERESIS }),
    ...(schedule.LOWER_HYSTERESIS != null && { hist_inf: schedule.LOWER_HYSTERESIS }),
    ...(schedule.LTC_CMD != null && { ltc_cmd: schedule.LTC_CMD as Command }),
    ...(schedule.LTC != null && { ltc_degree: schedule.LTC }),
    ...(schedule.LTI_CMD != null && { lti_cmd: schedule.LTI_CMD as Command }),
    ...(schedule.LTI != null && { lti_degree: schedule.LTI }),
  };
}

export function buildDut4ProgrammingCommand(schedules: ScheduleDut4[]): ScheduleDut4Command {
  const activeSchedules = schedules.filter((s) => !s.DELETE && s.SCHEDULE_STATUS);

  const daysSet = new Set<string>();
  for (const schedule of activeSchedules) {
    for (const [day, active] of Object.entries(schedule.DAYS)) {
      if (active) daysSet.add(day);
    }
  }

  return {
    days: Array.from(daysSet),
    schedule: activeSchedules.map((schedule) => ({
      start: schedule.BEGIN_TIME,
      end: schedule.END_TIME,
      mode: [scheduleDut4ToMode(schedule)],
    })),
  };
}

export function checkNeedMultipleScheduleDut4(schedules: ScheduleDut4[]): boolean {
  const activeSchedules = schedules.filter((s) => !s.DELETE && s.SCHEDULE_STATUS);
  for (let i = 0; i < activeSchedules.length; i++) {
    for (let j = i + 1; j < activeSchedules.length; j++) {
      if (checkWeekDayCommun(activeSchedules[i], activeSchedules[j])) {
        return true;
      }
    }
  }
  return false;
}

function checkWeekDayCommun(schedule1: ScheduleDut4, schedule2: ScheduleDut4) {
  return (
    (schedule1.DAYS.mon && schedule2.DAYS.mon)
    || (schedule1.DAYS.tue && schedule2.DAYS.tue)
    || (schedule1.DAYS.wed && schedule2.DAYS.wed)
    || (schedule1.DAYS.thu && schedule2.DAYS.thu)
    || (schedule1.DAYS.fri && schedule2.DAYS.fri)
    || (schedule1.DAYS.sat && schedule2.DAYS.sat)
    || (schedule1.DAYS.sun && schedule2.DAYS.sun)
  );
}

const MAX_CARDS_PER_DAY_DUT4 = 6;
const DAY_TRANSLATION_KEYS_DUT4: Record<keyof ScheduleDut4['DAYS'], string> = {
  mon: 'segundaFeira',
  tue: 'tercaFeira',
  wed: 'quartaFeira',
  thu: 'quintaFeira',
  fri: 'sextaFeira',
  sat: 'sabado',
  sun: 'domingo',
};

export function checkDailyLimitDut4(schedules: ScheduleDut4[], excludeIndex?: number): boolean {
  const active = schedules.filter((s, i) => !s.DELETE && i !== excludeIndex);
  const days = Object.keys(DAY_TRANSLATION_KEYS_DUT4) as (keyof ScheduleDut4['DAYS'])[];
  for (const day of days) {
    const count = active.filter((s) => s.DAYS[day]).length;
    if (count > MAX_CARDS_PER_DAY_DUT4) {
      toast.error(t('limiteProgramacoesPorDiaAtingido', { dia: t(DAY_TRANSLATION_KEYS_DUT4[day]) }));
      return false;
    }
  }
  return true;
}

export function checkCardsNoConflictsDut4(schedules: ScheduleDut4[]): boolean {
  const activeSchedules = schedules.filter((s) => !s.DELETE);
  let index = 0;
  for (const schedule of activeSchedules) {
    for (let i = index + 1; i < activeSchedules.length; i++) {
      const scheduleCompare = activeSchedules[i];
      const bothActive = schedule.SCHEDULE_STATUS && scheduleCompare.SCHEDULE_STATUS;
      if (checkWeekDayCommun(schedule, scheduleCompare) && bothActive) {
        const conflict = (schedule.BEGIN_TIME >= scheduleCompare.BEGIN_TIME && schedule.BEGIN_TIME < activeSchedules[i].END_TIME)
          || (schedule.END_TIME >= scheduleCompare.BEGIN_TIME && schedule.END_TIME < scheduleCompare.END_TIME)
          || (scheduleCompare.BEGIN_TIME >= schedule.BEGIN_TIME && scheduleCompare.BEGIN_TIME < schedule.END_TIME)
          || (scheduleCompare.END_TIME >= schedule.BEGIN_TIME && scheduleCompare.END_TIME < schedule.END_TIME);
        if (conflict) {
          toast.error(t('erroSalvarProgramacoesHorarias', {
            value1: schedule.SCHEDULE_TITLE,
            value2: scheduleCompare.SCHEDULE_TITLE,
          }));
          return false;
        }
      }
    }
    index++;
  }
  return true;
}

// Salva a programação no backend. O backend irá armazenar a programação e disponibilizar para o DUT4 quando necessário, mas não irá processar ou converter a programação, apenas armazenar os dados conforme enviados.
export async function saveSchedulesDut4(DUT_ID: string, CLIENT_ID: number, UNIT_ID: number, scheduleData: ScheduleDut4[]): Promise<void> {
  try {
    await apiCall('/dut/set-dut4-schedules', {
      DUT_ID,
      CLIENT_ID,
      UNIT_ID,
      schedules: scheduleData,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
}

export async function saveExceptionsDut4(DUT_ID: string, CLIENT_ID: number, UNIT_ID: number, exceptions: ExceptionDut4[]): Promise<void> {
  try {
    await apiCall('/dut/set-dut4-exceptions', {
      DUT_ID,
      CLIENT_ID,
      UNIT_ID,
      exceptions,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
}

// Verifica conflitos de horário entre exceções DUT4, respeitando REPEAT_YEARLY.
// Exceções com REPEAT_YEARLY=true são tratadas como anuais e conflitam com qualquer
// exceção da mesma data (DD/MM), independente do ano.
export function checkExceptsCardsConflictsDut4(exceptions: ExceptionDut4[]): boolean {
  const active = exceptions.filter((e) => !e.DELETE);
  const dayExcepts: Record<string, { YEARLY: DayExcept[]; [year: string]: DayExcept[] }> = {};

  for (const exc of active) {
    if (!exc.EXCEPTION_DATE || !exc.BEGIN_TIME || !exc.END_TIME) continue;
    // EXCEPTION_DATE está no formato YYYY-MM-DD
    const dateDM = `${exc.EXCEPTION_DATE.substring(8, 10)}/${exc.EXCEPTION_DATE.substring(5, 7)}`;
    const dateY = exc.EXCEPTION_DATE.substring(0, 4);

    if (!dayExcepts[dateDM]) dayExcepts[dateDM] = { YEARLY: [] };

    const entry: DayExcept = { BEGIN_TIME: exc.BEGIN_TIME, END_TIME: exc.END_TIME };
    if (exc.REPEAT_YEARLY) {
      dayExcepts[dateDM].YEARLY.push(entry);
    } else {
      if (!dayExcepts[dateDM][dateY]) dayExcepts[dateDM][dateY] = [];
      dayExcepts[dateDM][dateY].push(entry);
    }
  }

  for (const dateDM of Object.keys(dayExcepts)) {
    const yearlyExcepts = dayExcepts[dateDM].YEARLY;
    if (hasHoursConflicts(yearlyExcepts)) {
      toast.error(t('erroExcecoesConflitantes'));
      return false;
    }
    for (const year of Object.keys(dayExcepts[dateDM])) {
      if (year === 'YEARLY') continue;
      if (hasHoursConflicts([...dayExcepts[dateDM][year], ...yearlyExcepts])) {
        toast.error(t('erroExcecoesConflitantes'));
        return false;
      }
    }
  }

  return true;
}
